// src/components/fix-actions/redesign/suggested-actions-view.tsx
//
// Redesigned Suggested Actions tab body: secondary filter/search bar, split
// (list rail + detail) and accordion layouts. Consumes the page's already
// filtered actions and drives the page filter/search state.

import { useEffect, useMemo, useState } from "react";
import type { FixAction } from "../../../services/fix-actions/fix-actions-service";
import { sourceBadge, typeColor, Ic } from "./shared";
import { ActionDetailPanel, type DetailCallbacks } from "./action-detail-panel";
import { ExplainPopover, type ExplainAnchor } from "./explain-popover";

type Layout = "split" | "accordion";

const FILTER_OPTIONS = [
  { k: "all", label: "All actions" },
  { k: "high-impact", label: "High impact" },
  { k: "quick-wins", label: "Quick wins" },
  { k: "foundational", label: "Foundational" },
];

function accentFor(a: FixAction) {
  if (String(a.priority).toUpperCase() === "HIGH") return "var(--infrared-coral)";
  return typeColor(a.strategicTheme || "") === "var(--muted-foreground)" ? "var(--signal-blue)" : "var(--alpha-teal)";
}

export interface SuggestedActionsViewProps {
  actions: FixAction[];
  filterCategory: string;
  onFilterChange: (v: string) => void;
  searchQuery: string;
  onSearchChange: (v: string) => void;
  onGenerateAsset: DetailCallbacks["onGenerateAsset"];
  onExplainWithAI: (topic: string) => void;
  onNavigateToStudio?: (artifactId: string) => void;
  highlightActionTitle?: string | null;
  ready: boolean;
  // optional controlled selection (shell owns it so the agent thread can drive it)
  selectedId?: number | null;
  onSelectAction?: (id: number) => void;
  layout?: Layout;
  onLayoutChange?: (l: Layout) => void;
}

export function SuggestedActionsView(props: SuggestedActionsViewProps) {
  const { actions, filterCategory, onFilterChange, searchQuery, onSearchChange, ready } = props;
  const [internalLayout, setInternalLayout] = useState<Layout>("split");
  const layout = props.layout ?? internalLayout;
  const setLayout = props.onLayoutChange ?? setInternalLayout;
  // Force accordion below tablet width: the split rail's 360px min can't sit
  // next to the detail panel on a phone. The toggle is hidden while narrow.
  const [isNarrow, setIsNarrow] = useState<boolean>(() => typeof window !== "undefined" && window.innerWidth < 860);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 860);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const effectiveLayout: Layout = isNarrow ? "accordion" : layout;
  const [filterMenu, setFilterMenu] = useState(false);
  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(actions[0]?.id ?? null);
  const selectedId = props.selectedId !== undefined ? props.selectedId : internalSelectedId;
  const setSelectedId = (id: number) => (props.onSelectAction ?? setInternalSelectedId)(id);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [explain, setExplain] = useState<ExplainAnchor | null>(null);

  // keep a valid selection as the filtered set changes
  useEffect(() => {
    if (actions.length === 0) return;
    if (!actions.some((a) => a.id === selectedId)) setSelectedId(actions[0].id);
  }, [actions, selectedId]);

  // deep-link highlight from other modules
  useEffect(() => {
    if (!props.highlightActionTitle) return;
    const match = actions.find((a) => a.title.trim().toLowerCase() === props.highlightActionTitle!.trim().toLowerCase());
    if (match) {
      setSelectedId(match.id);
      setLayout("split");
    }
  }, [props.highlightActionTitle, actions]);

  const callbacks: DetailCallbacks = useMemo(
    () => ({
      onGenerateAsset: props.onGenerateAsset,
      onExplainType: (a) => setExplain(a),
      onExplainWithAI: props.onExplainWithAI,
    }),
    [props.onGenerateAsset, props.onExplainWithAI]
  );

  const selected = actions.find((a) => a.id === selectedId) || actions[0] || null;
  const filterLabel = FILTER_OPTIONS.find((o) => o.k === filterCategory)?.label || "All actions";

  const viewBtn = (active: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 11px",
    borderRadius: 8,
    cursor: "pointer",
    border: `1px solid ${active ? "var(--alpha-teal-30, var(--alpha-teal))" : "var(--border)"}`,
    background: active ? "var(--alpha-teal-5)" : "var(--background)",
    color: active ? "var(--alpha-teal)" : "var(--muted-foreground)",
  });

  const listRow = (a: FixAction, isSelected: boolean, onClick: () => void) => {
    const sb = sourceBadge(a.sourceModule);
    return (
      <button
        key={a.id}
        onClick={onClick}
        className="ai-row"
        style={{
          width: "100%",
          textAlign: "left",
          borderRadius: 12,
          cursor: "pointer",
          padding: "18px",
          marginBottom: 10,
          background: isSelected ? "var(--background)" : "transparent",
          border: `1px solid ${isSelected ? "var(--border)" : "transparent"}`,
          boxShadow: isSelected ? "var(--elevation-sm)" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
          <span style={{ width: 3, alignSelf: "stretch", minHeight: 64, borderRadius: 2, flexShrink: 0, background: accentFor(a) }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 7 }}>
              <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, lineHeight: 1.4, letterSpacing: "-.01em", color: "var(--foreground)" }}>{a.title}</div>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--muted-foreground)", lineHeight: 1.55, marginBottom: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {a.description}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: sb.color }}>
                <span style={{ width: 6, height: 6, borderRadius: 9999, background: sb.color, flexShrink: 0 }} />
                {sb.label}
              </span>
              <span style={{ width: 3, height: 3, borderRadius: 9999, background: "var(--silver-mist)" }} />
              <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>{a.strategicTheme}</span>
              <span style={{ width: 3, height: 3, borderRadius: 9999, background: "var(--silver-mist)" }} />
              <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>{a.artifacts?.length ?? 0} assets</span>
              {(a.artifacts?.length ?? 0) > 0 && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    const first = a.artifacts![0];
                    props.onGenerateAsset(a.id, a.title, a.strategicTheme || "", first.title, first.type, first.description || "", 0, a.artifacts!.length);
                  }}
                  className="ai-rowaction"
                  style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "var(--alpha-teal)", background: "var(--background)", border: "1px solid var(--alpha-teal-30, var(--alpha-teal))", borderRadius: 7, padding: "4px 10px", cursor: "pointer", whiteSpace: "nowrap" }}
                >
                  Generate
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  };

  return (
    <div>
      {/* secondary bar */}
      <div style={{ padding: "14px clamp(16px,2vw,24px) 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--muted-foreground)" }}>{actions.length} actions</span>
        <div style={{ position: "relative" }}>
          <button
            onClick={(e) => { e.stopPropagation(); setFilterMenu((v) => !v); }}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 500, color: "var(--foreground)", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 12px", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <Ic paths={["M3 6h18M7 12h10M11 18h2"]} size={14} sw={1.7} color="var(--muted-foreground)" />
            {filterLabel}
            <Ic paths={["m6 9 6 6 6-6"]} size={12} sw={2} />
          </button>
          {filterMenu && (
            <>
              <div onClick={() => setFilterMenu(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 50, minWidth: 190, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--elevation-md)", padding: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", padding: "6px 10px 5px" }}>Show</div>
                {FILTER_OPTIONS.map((o) => {
                  const active = filterCategory === o.k;
                  return (
                    <button
                      key={o.k}
                      onClick={() => { onFilterChange(o.k); setFilterMenu(false); }}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", textAlign: "left", fontSize: 13, fontWeight: active ? 600 : 500, color: active ? "var(--alpha-teal)" : "var(--foreground)", background: active ? "var(--teal-pill-bg, var(--alpha-teal-10))" : "transparent", border: "none", borderRadius: 7, padding: "8px 10px", cursor: "pointer" }}
                    >
                      {o.label}
                      {active && <Ic paths={["m5 12 5 5L20 7"]} size={14} sw={2.4} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", display: "inline-flex" }}>
            <Ic paths={["M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0", "m21 21-4.3-4.3"]} size={15} sw={1.7} color="var(--muted-foreground)" />
          </span>
          <input
            className="ai-input"
            type="text"
            placeholder="Search actions..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ width: 220, maxWidth: "46vw", padding: "8px 12px 8px 34px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--background)", fontSize: 13, fontFamily: "var(--font-sans)", color: "var(--foreground)", outline: "none" }}
          />
        </div>
        {!isNarrow && (
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            <button onClick={() => setLayout("split")} style={viewBtn(layout === "split")}>
              <Ic paths={["M3 3h7v18H3z", "M14 3h7v18h-7z"]} size={12} sw={1.8} />
              Split
            </button>
            <button onClick={() => setLayout("accordion")} style={viewBtn(layout === "accordion")}>
              <Ic paths={["M3 5h18M3 12h18M3 19h18"]} size={12} sw={1.8} />
              Accordion
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: "0 clamp(16px,2vw,24px) 40px" }}>
        {actions.length === 0 ? (
          <div style={{ border: "1px dashed var(--silver-mist)", borderRadius: 16, background: "var(--muted)", padding: "52px 32px", textAlign: "center", color: "var(--muted-foreground)", fontSize: 13.5 }}>
            {/* No filter/search narrowing means there are simply no actions yet: a first-run state, not a filter miss. */}
            {filterCategory === "all" && searchQuery.trim() === ""
              ? "No actions yet. They appear after your first measurement run."
              : "No actions match this filter or search."}
          </div>
        ) : effectiveLayout === "split" ? (
          <div style={{ display: "flex", gap: 0, border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-sm)", minHeight: 560 }}>
            <div className="ai-scroll" style={{ flex: "0 0 clamp(360px, 40%, 560px)", minWidth: 0, background: "var(--muted)", borderRight: "1px solid var(--border)", overflowY: "auto", maxHeight: 720, padding: 12 }}>
              {actions.map((a) => listRow(a, a.id === selectedId, () => setSelectedId(a.id)))}
            </div>
            <div className="ai-scroll" style={{ flex: 1, minWidth: 0, background: "var(--background)", overflowY: "auto", maxHeight: 720 }}>
              {selected && <ActionDetailPanel action={selected} ready={ready} callbacks={callbacks} />}
            </div>
          </div>
        ) : (
          <div style={{ border: "1px solid var(--border)", borderRadius: 14, overflow: "hidden", boxShadow: "var(--elevation-sm)", background: "var(--muted)", padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
            {actions.map((a) => {
              const isOpen = !!expanded[a.id];
              const sb = sourceBadge(a.sourceModule);
              return (
                <div key={a.id} style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "var(--background)" }}>
                  <button onClick={() => setExpanded((s) => ({ ...s, [a.id]: !s[a.id] }))} style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", cursor: "pointer", padding: "11px 14px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
                      <span style={{ width: 3, alignSelf: "stretch", minHeight: 34, borderRadius: 2, flexShrink: 0, background: accentFor(a) }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 5 }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 6px", borderRadius: 5, fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", background: sb.bg, color: sb.color, border: `1px solid ${sb.border}` }}>
                            <Ic paths={sb.icon} size={10} sw={1.7} /> {sb.label}
                          </span>
                          <span style={{ fontSize: 10, fontWeight: 600, background: "var(--muted)", color: "var(--muted-foreground)", border: "1px solid var(--border)", borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap" }}>{a.strategicTheme}</span>
                          <span style={{ fontSize: 10, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>{a.artifacts?.length ?? 0} assets</span>
                          <span style={{ marginLeft: "auto", color: "var(--muted-foreground)", display: "inline-flex", transition: "transform var(--duration-base)", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>
                            <Ic paths={["m6 9 6 6 6-6"]} size={15} sw={2} />
                          </span>
                        </div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, color: "var(--foreground)", marginBottom: 3 }}>{a.title}</div>
                        {!isOpen && <div style={{ fontSize: 11.5, color: "var(--muted-foreground)", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.description}</div>}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div style={{ borderTop: "1px solid var(--border)" }}>
                      <ActionDetailPanel action={a} ready={ready} callbacks={callbacks} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ExplainPopover anchor={explain} onClose={() => setExplain(null)} />
    </div>
  );
}
